import React, { useState, useEffect, useRef } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Stack, Box, Container, Card, Grid, Button, Alert, FormLabel, LinearProgress, Divider, ButtonGroup, IconButton } from "@mui/material";
import Api from "../../network/Api";
import ScrollControl from "../Shared/ScrollControl";
import TypingIndicator from "../Shared/TypingIndicator";
import Markdown from 'react-markdown';
import wss from '../../channels/consumer';
import { Subscription } from "@rails/actioncable"
import { ConversationMessage } from "../../types/Conversation";
import SendButton from "./SendButton";
import { Lesson } from "../../types/Lessons";
import Questionaire, { Scores } from "../Lessons/Questionaire";
import Summary from "../Lessons/Summary";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FeedbackButtons from "../Shared/Feedback";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
 
const Chat = (props: { prompt?: string }) => {

  const [history, setHistory] = useState<Array<ConversationMessage>>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [currentLesson, setCurrentLesson] = useState<number>();
  const [prompt, setPrompt] = useState<string>(props.prompt || "");
  const [isAsking, shouldAsk] = useState<boolean>(false);
  const [isSkipable, canSkip] = useState<boolean>(false);
  const [inputDisabled, disableInput] = useState<boolean>(false);
  const [showsToolbox, showToolbox] = useState<boolean>(false);
  const [runError, raiseRunError] = useState<boolean>(false);
  const [shouldContinue, askToContinue] = useState<boolean>(false);

  const { conversationId } = useParams();
  const subscription = useRef<Subscription>(null);

  const scrollBottomRef = useRef();

  const randomId = (): string => {
    return `${Math.floor(Math.random() * 100)}`;
  }

  useEffect(() => {
    subscription.current = wss.subscriptions.create({channel: "ConversationChannel", conversation_id: conversationId}, {
      connected() {
        if (prompt) {
          submitMessage();
        } else {
          Api.get(`/conversations/${conversationId}`).then(result => {
            setHistory(result.messages?.reverse());
            setLessons(result.lessons);
          })
        }
      },
      received(data) {
        switch(data.type) {
          case "chunk":
            upsertMessage(data.message, true);
            break;
          case "continue":
            askToContinue(true);
            break;
          case "lessons":
            setLessons(data.lessons.items);
            break;
          case "error":
            raiseRunError(true);
            break;
          default: break;
        }
      }
    });

    return () => {
      subscription.current?.unsubscribe();
      subscription.current = undefined;
    }
  }, []);

  useEffect(() => {
    disableInput(isAsking);
  }, [isAsking]);

  useEffect(() => {
    if (!shouldContinue || isAsking) { return }
    shouldAsk(true);
    askToContinue(false);
    Api.put(`/conversations/${conversationId}`, { role: 'system', message: 'continue' }).then(onResponse);
  }, [isAsking, shouldContinue]);

  const skip = () => {
    if (!isSkipable || isAsking) { return }
    shouldAsk(true);
    canSkip(false);
    Api.put(`/conversations/${conversationId}`, { role: 'system', message: 'skip' }).then(onResponse);
  };

  const submitMessage = () => {
    if (prompt === "") { return }
    shouldAsk(true);
    setHistory(current => { return [...current, { id: randomId(), role: "user", content: prompt }] });
    setPrompt("");
    Api.put(`/conversations/${conversationId}`, { role: 'user', message: prompt }).then(onResponse);
  }

  const cancel = (retry: boolean) => {
    Api.delete(`/conversations/${conversationId}?retry=${retry}`).then(response => {
      if (typeof(response) !== "undefined") {
        onResponse(response);
      }
    });
  }

  const upsertMessage = (message: ConversationMessage, chunk: boolean) => {
    setHistory((current) => {
      let changedHistory = false;
      let newItems = current.map((item) => {
        if (item.id !== message.id) {
          return item;
        }
        changedHistory = true;
        return {...item, content: (chunk ? item.content + message.content : message.content)};
      });
      if (!changedHistory) {
        newItems = [...newItems, message];
      }
      return newItems;
    });
  }

  const onResponse = (result) => {
    shouldAsk(isAsking => {
      if (isAsking) {
        upsertMessage(result, false);
      }
      return false;
    })
  }

  useEffect(() => {
    if (scrollBottomRef.current) {
      scrollBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  useEffect(() => {
    if (lessons.length <= 0) { return }
    setCurrentLesson(prevIndex => {
      if (typeof prevIndex !== "number") {
        return 0;
      }
      if (prevIndex < lessons.length - 1) {
        return prevIndex + 1;
      }
      return prevIndex;
    })
  }, [lessons]);

  useEffect(() => {
    showToolbox(typeof(currentLesson) !== "undefined");
    canSkip(typeof(currentLesson) === "undefined");
  }, [currentLesson]);

  const updateScores = (lesson: Lesson, scores: Scores) => {
    shouldAsk(true);
    Api.put(`/lessons/${lesson.id}`, scores).then(result => {
      setLessons(prevLessons => 
        prevLessons.map(l => (l.id === result.id ? result : l))
      );
      
      shouldAsk(false);
    });
  }

  const renderSection = (item: ConversationMessage) => {
    switch(item.role) {
      case "assistant":
        return <Markdown>{item.content}</Markdown>
      case "user":
        return <Stack direction="row" justifyContent="flex-end"><Card elevation={0} sx={{ maxWidth: '75%', padding: "0.75rem 1rem !important", backgroundColor: "var(--color-greyDark) !important" }}><p>{item.content}</p></Card></Stack>
      default: break;
    }
  }

  return (
    <Stack
      className="animate__animated animate__fadeIn"
      marginY={"auto"}
      flexGrow={1}
      width="100%"
    >
      <Stack
        sx={{
          flex: "1 1 0",
          overflowY: "auto",
          pt: 4,
          alignItems: "center",
        }}
      >
        <Container maxWidth={showsToolbox ? "xl" : "md"}>
          <Grid container>
            <Grid size={{xs: 12, md: showsToolbox ? 5 : 12}}>
              <Stack spacing={3} sx={{position: "relative"}}>
                <ScrollControl backgroundColor="greyLight"><>
                
                  { history.map(item => <Box key={item.id}>{renderSection(item)}</Box>) }

                  { typeof(currentLesson) !== "undefined" && <>
                    { lessons.length > 1 && <>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <FormLabel><strong>{(currentLesson+1)}/{lessons.length}</strong></FormLabel>
                        <LinearProgress sx={{flexGrow: 1}} color="info" variant="determinate" value={(currentLesson+1)/lessons.length*100} />
                        <ButtonGroup>
                          <IconButton color="info" disabled={currentLesson < 1} onClick={() => setCurrentLesson(currentLesson-1)}><NavigateBeforeIcon /></IconButton>
                          <IconButton color="info" disabled={currentLesson >= lessons.length - 1} onClick={() => setCurrentLesson(currentLesson + 1)}><NavigateNextIcon /></IconButton>
                        </ButtonGroup>
                      </Stack>
                    </> }
                    
                    <Box className='animate__animated animate__fadeIn'>
                      <Questionaire key={lessons[currentLesson].id}
                                    lesson={lessons[currentLesson]}
                                    multiple={lessons.length > 1}
                                    onCompletion={scores => {
                                      updateScores(lessons[currentLesson], scores);
                                    }} />
                    </Box>

                    { lessons.filter(lesson => lesson.impact_score).length == lessons.length && <Button component={NavLink} variant="outlined" startIcon={<TaskAltIcon />} to={`/lessons/new`}>Capture new lesson</Button> }
                  </> }

                  { conversationId && !isAsking && history[history.length - 1]?.role === "assistant" && <FeedbackButtons conversationId={conversationId} messageId={history[history.length - 1].id} /> }

                  { isAsking && <TypingIndicator /> }

                  { runError && <Alert severity="error"
                                        action={
                                          <Button color="error" size="small" onClick={() => cancel(true)}>Re-run</Button>
                                        }>Something went wrong upon your request.<br />Please try again.</Alert>}

                  {/* { isSkipable && !isAsking && <Stack direction="row" justifyContent='flex-end' alignItems='center'>
                    <FormLabel><small>Root cause already fully identified?</small></FormLabel>
                    <Button size="small" variant="text" color="info" onClick={skip}>Skip to next step</Button>
                  </Stack> } */}

                  <Box ref={scrollBottomRef}></Box>

                </></ScrollControl>
              </Stack>
            </Grid>

            { showsToolbox && typeof(currentLesson) !== "undefined" && <Grid size={{xs: 12, md: 7}}>
              <Card sx={{position: "sticky", left: 0, top: 0, marginLeft: { xs: 0, md: 6 }, right: 0, height: { md: "calc(100vh - 228px - 6rem)" }}} className="animate__animated animate__fadeInUp">
                <Box sx={{height: "100%", overflow: "hidden", mr: -3}}>
                  <Box sx={{overflowY: 'auto', height: "100%", pr: 2}}>
                    <ScrollControl backgroundColor="white">
                      <Summary lesson={lessons[currentLesson]} 
                               animated preview
                               onChange={() => {}}
                               onSave={() => {}} />
                    </ScrollControl>
                  </Box>
                </Box>
              </Card>
            </Grid> }

          </Grid>
        </Container>
      </Stack>

      <Container maxWidth={showsToolbox ? "xl" : "md"} sx={{ mb: 3 }}>
        <Grid container>
          <Grid size={{xs: 12, md: showsToolbox ? 5 : 12}}>
            <SendButton value={prompt}
                        disabled={inputDisabled}
                        isRunning={isAsking}
                        onChange={setPrompt}
                        onCancel={() => cancel(false)}
                        onClick={submitMessage} />
            <footer className="text-secondary text-center"><small>AI can make mistakes. Consider fact checking important information.</small></footer>
          </Grid>
        </Grid>
      </Container>
    </Stack>
  );
};

export default Chat;
